// Keeps the ADMIN-ONLY "Public photos" layer on the page's map: added when the
// map loads, re-added after every base-map style switch (which drops custom
// sources and layers), removed when the overlay's card is switched off, and
// never added for a viewer who isn't an admin — the /vector proxy would only
// answer 403. The same shape as trailHazards/hazardLayerController.ts.
//
// One per page, like `window.map`: every shared map dialog's Overlays tab
// mounts a PublicPhotosMapHost that acquires it, and the layer goes away when
// the last one releases it.
//
// Collaborators are injected so the decisions are unit-tested with a fake map
// and registry; `sharedPublicPhotosLayer()` wires the real ones.

import emitter from '../bus';
import {
  PUBLIC_PHOTOS_LAYER_CATEGORY,
  PUBLIC_PHOTOS_LAYER_ID,
  PUBLIC_PHOTOS_LAYER_Z,
  PUBLIC_PHOTOS_SOURCE_ID,
  isAdminViewer,
  photoIdsAtPoint,
  publicPhotosLayerSpec,
  publicPhotosSourceSpec,
} from './publicPhotosLayerSpec';

/** On the bus whenever the switch changes, so every card on the page agrees. */
export const PUBLIC_PHOTOS_VISIBILITY_EVENT = 'admin-public-photos:visibility-changed';

/** Per browser, like the other overlay switches. Off until an admin turns it on. */
export const PUBLIC_PHOTOS_VISIBLE_STORAGE_KEY = 'adminPublicPhotosVisible';

export interface PublicPhotosLayerMap {
  on(type: string, layerOrListener: any, listener?: any): void;
  off(type: string, layerOrListener: any, listener?: any): void;
  once?(type: string, listener: any): void;
  getSource(id: string): any;
  getLayer(id: string): any;
  getCanvas(): { style: { cursor: string } };
  queryRenderedFeatures(geometry: unknown, options: { layers: string[] }): unknown[];
}

export interface PublicPhotosLayerRegistry {
  addSource(id: string, spec: any, replace?: boolean): void;
  addLayer(layer: any, category: any, opts?: { z?: number }): void;
  removeLayer(id: string, removeOrphanSource?: boolean): void;
}

export interface EventBus {
  on(event: string, handler: (...args: any[]) => void): void;
  off(event: string, handler: (...args: any[]) => void): void;
  emit(event: string, payload?: any): void;
}

export interface PublicPhotosLayerDeps {
  getMap: () => PublicPhotosLayerMap | null | undefined;
  getRegistry: () => PublicPhotosLayerRegistry | null | undefined;
  isAdmin: () => boolean;
  origin: () => string;
  bus: EventBus;
  readVisible: () => boolean;
  writeVisible: (enabled: boolean) => void;
}

/** Opens the dialog for the photo ids stacked under a click (top-most first). */
export type PublicPhotosClickHandler = (ids: string[]) => void;
export type PublicPhotosClickFilter = (event: any) => boolean;

export class PublicPhotosLayerController {
  private enabled: boolean;
  private holders = 0;
  private boundMap: PublicPhotosLayerMap | null = null;
  private clickHandler: PublicPhotosClickHandler | null = null;
  private clickFilter: PublicPhotosClickFilter | null = null;
  private retryScheduled = false;
  /** The page's own cursor (the planner's crosshair), put back when the pointer leaves a dot. */
  private cursorBeforeHover = '';

  constructor(private readonly deps: PublicPhotosLayerDeps) {
    this.enabled = deps.readVisible();
  }

  /* ---------- lifecycle ---------- */

  acquire(): void {
    this.holders += 1;
    if (this.holders > 1) return;
    this.deps.bus.on('map-loaded', this.sync);
    this.deps.bus.on('map-style-loaded', this.sync);
    this.sync();
  }

  release(): void {
    if (this.holders === 0) return;
    this.holders -= 1;
    if (this.holders > 0) return;
    this.deps.bus.off('map-loaded', this.sync);
    this.deps.bus.off('map-style-loaded', this.sync);
    this.unbind();
    this.removeLayer();
  }

  /* ---------- state ---------- */

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Whether the overlay can show at all for this viewer (an admin). */
  isAvailable(): boolean {
    return this.deps.isAdmin();
  }

  setEnabled(enabled: boolean): void {
    const next = !!enabled;
    if (next === this.enabled) return;
    this.enabled = next;
    this.deps.writeVisible(next);
    this.deps.bus.emit(PUBLIC_PHOTOS_VISIBILITY_EVENT, next);
    this.sync();
  }

  setClickHandler(handler: PublicPhotosClickHandler | null): void {
    this.clickHandler = handler;
  }

  /** Return true to ignore a click, e.g. while the planner is drawing a route. */
  setClickFilter(filter: PublicPhotosClickFilter | null): void {
    this.clickFilter = filter;
  }

  /* ---------- map ---------- */

  /** Brings the map in line with the switch: layer on, or layer off. Idempotent. */
  readonly sync = (): void => {
    if (this.holders === 0) return;
    const map = this.deps.getMap();
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;

    if (!this.enabled || !this.deps.isAdmin()) {
      this.removeLayer();
      return;
    }

    try {
      registry.addSource(PUBLIC_PHOTOS_SOURCE_ID, publicPhotosSourceSpec(this.deps.origin()));
      registry.addLayer(publicPhotosLayerSpec(), PUBLIC_PHOTOS_LAYER_CATEGORY, { z: PUBLIC_PHOTOS_LAYER_Z });
      this.bind(map);
    } catch (error) {
      // Most often "Style is not done loading": retried on map-style-loaded, or
      // once when a map that had already loaded next goes idle.
      console.warn('[admin_public_photos] could not add the layer yet', error);
      this.scheduleRetry(map);
    }
  };

  private scheduleRetry(map: PublicPhotosLayerMap): void {
    if (this.retryScheduled || typeof map.once !== 'function') return;
    this.retryScheduled = true;
    map.once('idle', () => {
      this.retryScheduled = false;
      this.sync();
    });
  }

  private removeLayer(): void {
    const map = this.deps.getMap();
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;
    try {
      if (map.getLayer(PUBLIC_PHOTOS_LAYER_ID)) registry.removeLayer(PUBLIC_PHOTOS_LAYER_ID, true);
    } catch (error) {
      console.warn('[admin_public_photos] could not remove the layer', error);
    }
  }

  private readonly onClick = (event: any): void => {
    if (this.clickFilter?.(event)) return;
    const ids = photoIdsAtPoint(this.boundMap, event?.point);
    if (ids.length && this.clickHandler) this.clickHandler(ids);
  };

  private readonly onMouseEnter = (): void => {
    const map = this.boundMap;
    if (!map) return;
    const canvas = map.getCanvas();
    if (canvas.style.cursor !== 'pointer') this.cursorBeforeHover = canvas.style.cursor;
    canvas.style.cursor = 'pointer';
  };

  private readonly onMouseLeave = (): void => {
    const map = this.boundMap;
    if (map) map.getCanvas().style.cursor = this.cursorBeforeHover;
  };

  /** A style change not announced on the bus (the planner's ?mapStyle=, the park map's late setStyle). */
  private readonly onStyleLoad = (): void => {
    this.sync();
  };

  // Layer-scoped listeners are delegated by layer id, so they outlive a style
  // switch — bound once per map instance, not once per re-add.
  private bind(map: PublicPhotosLayerMap): void {
    if (this.boundMap === map) return;
    this.unbind();
    map.on('click', PUBLIC_PHOTOS_LAYER_ID, this.onClick);
    map.on('mouseenter', PUBLIC_PHOTOS_LAYER_ID, this.onMouseEnter);
    map.on('mouseleave', PUBLIC_PHOTOS_LAYER_ID, this.onMouseLeave);
    map.on('style.load', this.onStyleLoad);
    this.boundMap = map;
  }

  private unbind(): void {
    const map = this.boundMap;
    if (!map) return;
    map.off('click', PUBLIC_PHOTOS_LAYER_ID, this.onClick);
    map.off('mouseenter', PUBLIC_PHOTOS_LAYER_ID, this.onMouseEnter);
    map.off('mouseleave', PUBLIC_PHOTOS_LAYER_ID, this.onMouseLeave);
    map.off('style.load', this.onStyleLoad);
    map.getCanvas().style.cursor = '';
    this.boundMap = null;
  }
}

function readStoredVisible(): boolean {
  try {
    return window.localStorage?.getItem(PUBLIC_PHOTOS_VISIBLE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeStoredVisible(enabled: boolean): void {
  try {
    window.localStorage?.setItem(PUBLIC_PHOTOS_VISIBLE_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // Private mode / blocked storage: the switch still works for this page view.
  }
}

let shared: PublicPhotosLayerController | null = null;

/** The page's controller, wired to `window.map`, `window.layerRegistry` and the session. */
export function sharedPublicPhotosLayer(): PublicPhotosLayerController {
  if (!shared) {
    shared = new PublicPhotosLayerController({
      getMap: () => (window as any).map,
      getRegistry: () => (window as any).layerRegistry,
      isAdmin: () => isAdminViewer((window as any).user, (window as any).admin),
      origin: () => window.location.origin,
      bus: emitter as unknown as EventBus,
      readVisible: readStoredVisible,
      writeVisible: writeStoredVisible,
    });
  }
  return shared;
}
